import { Link } from "react-router-dom";

import { env } from "~/env";
import { ROUTES } from "~/router/constants";

interface FAQData {
  [key: string]: {
    question: string;
    answer: string | string[] | React.ReactNode;
  }[];
}

export const FAQText: FAQData = {
  "About Elektra": [
    {
      question: "What is Elektra?",
      answer:
        "Elektra combines world-class clinical care with a private education platform to become your go-to resource for menopause care, support, and community…everything you need to thrive during this 8-10 year transition (and beyond!).",
    },
    {
      question: "How does Elektra work?",
      answer: [
        "Step 1: Schedule a visit with one of Elektra's menopause experts (clinician or coach).",
        "Step 2: Complete a menopause questionnaire identifying your top symptoms, burning menopause questions, health history, and goals.",
        "Step 3: Meet with your Elektra menopause expert for individualized support and to create a follow-up plan.",
        "Step 4: Access Elektra's private community for menopause education, virtual events, and more.",
      ],
    },
  ],
  "Clinical Care & Coaching": [
    {
      question: "What do you treat?",
      answer:
        "Elektra clinicians can treat menopause symptoms including hot flashes, sleep concerns, fatigue, vaginal dryness/painful sex, and more. We can also address menopause-adjacent health concerns such as high cholesterol, hypothyroidism, hypertension, anxiety/depression, etc.",
    },
    {
      question: "Does an Elektra clinician replace my gynecologist?",
      answer: [
        "Nope, Elektra is designed to complement but not replace your local care. Since we don't do in-person visits (just telemedicine), it's important for you to continue your care with a local gynecologist and/or primary care provider for regular pelvic exams, issues that require a physical evaluation, and any non-menopause concerns.",
        "Looking for a new provider for in-person care? Elektra clinicians can provide referrals as needed.",
      ],
    },
    {
      question:
        "I already have a gynecologist! Why should I also see a menopause clinician through Elektra?",
      answer:
        "Less than 20% of OB/GYNs receive menopause training in their residency programs, and others simply don't spend time on it during a visit. This means that many women who consult their clinicians don't receive the care they deserve (in fact, 70% of women who seek menopause care don't receive treatment). Elektra is trying to fill this gap with clinicians who are specially trained in menopause — although it's important to note that we are designed to complement, not replace, your in-person care.",
    },
    {
      question: "How does Elektra evaluate and treat menopause?",
      answer:
        "Elektra follows an evidence-based approach closely adhering to guidelines set forth by The Menopause Society (formerly The North American Menopause Society). This includes the use of FDA-approved hormonal and non-hormonal medications. While Elektra clinicians may order labs as appropriate, they do not order saliva or urine-based testing (i.e. DUTCH test).",
    },
    {
      question: "Who will I see at Elektra?",
      answer:
        "Your Elektra care team is made up of a clinician and a coach. Elektra's clinicians are women's health experts who have sub-specialized in menopause and women's health at midlife. They can see patients in select states and prescribe medication as needed. Coaches provide non-clinical support in the form of symptom-specific lifestyle recommendations (nutrition, exercise, etc.), education, and emotional support.",
    },
    {
      question:
        "What is an Elektra Coach and how are they different from an Elektra Clinician?",
      answer:
        "Unlike Elektra Clinicians who provide telemedicine care in select states (with labs and prescriptions), Coaches can see patients in all states for non-clinical support. The Coaches are healthcare professionals you can think of as part-accountability partners, part-menopause doulas. They work 1:1 with patients to help them feel educated and empowered during their menopause journey, which comes to life in the form of symptom-specific education, lifestyle interventions, supplement information, and product recommendations.",
    },
    {
      question: "How do I book a visit?",
      answer: (
        <>
          Booking is easy and can be done entirely online{" "}
          <Link
            to={ROUTES.hubBookVisit}
            className="text-violet underline hover:text-violet-700"
            aria-label="Book a visit here"
          >
            here
          </Link>
          . Prefer to book by phone? We can be reached at{" "}
          <a
            href="tel:6467606669"
            className="text-violet underline hover:text-violet-700"
          >
            {env.VITE_SUPPORT_PHONE_NUMBER}
          </a>
          .
        </>
      ),
    },
    {
      question: "Do you have an appointment waitlist for a sooner appointment?",
      answer: (
        <>
          Yes! If you'd like to be added to our appointment waitlist for a
          sooner appointment, please call or text our admin team at{" "}
          <a
            href="sms:646-760-6669"
            className="text-violet underline hover:text-violet-700"
          >
            646-760-6669
          </a>
          . We will ask you about your preferences for days, times, and
          providers, and then add you to the list. If a sooner slot that matches
          your preferences becomes available, we will contact you to see if you
          would like to schedule it.
        </>
      ),
    },
    {
      question: "How long will my visit be?",
      answer: [
        "Initial visits with our board-certified, menopause-trained clinicians are scheduled for 30 minutes, while follow-ups are 15.",
        "All coaching calls are 30 minutes.",
      ],
    },
    {
      question: "Where can I find my visit link?",
      answer: (
        <>
          You'll receive a link to join your video visit 1 hour before your
          appointment. If you don't receive a link or are having trouble joining
          your appointment, please text us at{" "}
          <a
            href="sms:6467606669"
            className="text-violet underline hover:text-violet-700"
          >
            646-760-6669
          </a>
          .
        </>
      ),
    },
    {
      question: "How do I contact my care team?",
      answer: (
        <>
          You can send your clinician and coach a secure message by logging into
          your patient portal{" "}
          <a
            href="https://31312-1.portal.athenahealth.com/"
            target="_blank"
            className="text-violet underline hover:text-violet-700"
            rel="noopener noreferrer"
            aria-label="Patient portal here"
          >
            here
          </a>
          . It may take 2-3 business days to receive a reply. If you have urgent
          needs during business hours, please text us at{" "}
          <a
            href="sms:6467606669"
            className="text-violet underline hover:text-violet-700"
          >
            646-760-6669
          </a>
          . In the case of a medical emergency, please dial{" "}
          <a
            href="tel:911"
            className="text-violet underline hover:text-violet-700"
          >
            911
          </a>
          .
        </>
      ),
    },
    {
      question: "How do Elektra clinicians work with my other clinicians?",
      answer:
        "Elektra clinicians are more than happy to collaborate with your existing care team and can communicate through call, fax, and email. How much, and what we share is entirely up you. We can do what works best for you and your care team, whether that's coordinating directly with a specialist, or providing you with a care summary that you can discuss at your routine physical.",
    },
    {
      question:
        "What's your cancellation policy? What if I need to reschedule?",
      answer: (
        <>
          Life happens…we totally get it! We just ask that you cancel or
          reschedule your clinical or coaching appointment at least 24 hours in
          advance to avoid incurring a ${env.VITE_LATE_CANCELLATION_FEE} charge.
          You can do so by emailing{" "}
          <a
            href={`mailto:${env.VITE_ELEKTRA_CARE_EMAIL}`}
            className="text-violet underline hover:text-violet-700"
          >
            {env.VITE_ELEKTRA_CARE_EMAIL}
          </a>{" "}
          or texting{" "}
          <a
            href="sms:6467606669"
            className="text-violet underline hover:text-violet-700"
          >
            (646) 760-6669
          </a>
          .
        </>
      ),
    },
    {
      question: "Who should I contact if I'm running late?",
      answer: (
        <>
          If you're late to your visit, you may incur a fee and will likely be
          asked to reschedule your appointment (we want to be sure your
          clinician/coach has enough time to spend with you!). If you are
          running a few minutes late, please text{" "}
          <a
            href="sms:6467606669"
            className="text-violet underline hover:text-violet-700"
          >
            (646) 760-6669
          </a>{" "}
          or email{" "}
          <a
            href={`mailto:${env.VITE_ELEKTRA_CARE_EMAIL}`}
            className="text-violet underline hover:text-violet-700"
          >
            {env.VITE_ELEKTRA_CARE_EMAIL}
          </a>{" "}
          to let us know.
        </>
      ),
    },
    {
      question: "Does Elektra see patients in all 50 states?",
      answer:
        "At the moment, Elektra is licensed to see patients for clinical care in New York, Connecticut, Massachusetts, Florida, Pennsylvania, New Jersey, Illinois, Arizona, Georgia, Iowa, Missouri, Nebraska, Ohio, Oklahoma, Tennessee, and Texas. Sign up for our weekly newsletter to stay in the loop on all things menopause and be the first to know when we expand access.",
    },
  ],
  "Medication & Labs": [
    {
      question: "Can I order products or hormones directly from Elektra?",
      answer:
        "Elektra clinicians can prescribe FDA-approved medications (both hormonal and non-hormonal) and recommend supplements. All prescriptions will be sent to your preferred pharmacy — we don't sell them directly.",
    },
    {
      question: "Can I get a medication refill without a visit?",
      answer:
        "If you're up to date on your follow-ups, then we'd be happy to refill your medication without seeing you for a visit. (Typically, once you're on medication and a stable dose has been established, we ask to see you for a follow-up every 6 months.)",
    },
    {
      question: "What labs can I get through Elektra?",
      answer: [
        "Patients ask us all the time…do you need to test my hormones before starting hormone therapy or during treatment?",
        "Major menopause guidelines actually do not recommend checking your estrogen levels to guide treatment. Why? Because hormone levels, especially during perimenopause, can be incredibly volatile. Estrogen can be high one day and really low the next. A single blood test is really just a snapshot of that moment. It's not really a reliable measure of what's happening overall. Plus, if your results come back \"normal\" but you're still experiencing symptoms, does that mean you don't deserve treatment? Of course not!",
        "And once someone starts on hormone therapy, there's also no proven target estrogen level that we're trying to hit. Two people can have the exact same number and feel completely different. So instead of chasing lab values, we focus on the things that actually matter: are your hot flashes and other symptoms improving? Are you sleeping better? Is your mood or brain fog improving? Hormone therapy is about treating symptoms and improving quality of life, not trying to hit a specific lab number.",
      ],
    },
    {
      question:
        "Does Elektra just prescribe hormone treatment? Or non-hormonal as well?",
      answer:
        "Elektra clinicians can prescribe both hormonal AND non-hormonal prescription treatments as well as evidence-backed supplements. We don't prescribe compounded or non-FDA-approved medications.",
    },
    {
      question: "Does Elektra prescribe testosterone?",
      answer:
        "At this time, a few of Elektra's clinical experts are able to prescribe testosterone to patients who reside in NY. A clinical care appointment is most appropriate to determine whether testosterone would be a safe and appropriate treatment option.",
    },
    {
      question: "Does Elektra prescribe GLP-1s?",
      answer:
        "Yes, menopause care at Elektra includes the treatment of menopause-related weight gain and metabolic syndrome through FDA-approved GLP-1 prescribing. A clinical care appointment is necessary to determine whether GLP-1s would be an appropriate treatment option.",
    },
    {
      question:
        "Do Elektra clinicians order saliva or urine-based testing (i.e. DUTCH test)?",
      answer:
        "Elektra clinicians do not order saliva or urine-based tests (i.e. DUTCH test) as it is not evidence-based, or FDA-approved.",
    },
  ],
  "Billing & Insurance": [
    {
      question: "Do you accept insurance?",
      answer: (
        <>
          Yes! Elektra is in-network with select insurance plans for clinical
          care and actively working to expand coverage with others. We also
          accept out-of-pocket patients and can provide a superbill you can
          submit to your insurance carrier for out-of-network reimbursement. And
          remember, even if Elektra isn't covered by your insurance plan, your
          labs and medications likely will be. (Select insurance plans also
          cover coaching.) Insurance is complicated, and co-pays, deductibles,
          and co-insurance vary depending on your plan. If you have any
          questions regarding your financial responsibility, we recommend
          contacting your insurance plan. You can also text Elektra's insurance
          verification number at{" "}
          <a
            href="sms:4148886578"
            className="text-violet underline hover:text-violet-700"
          >
            (414) 888-6578
          </a>{" "}
          for any questions regarding insurance. Just text an image of the front
          and back of your insurance card, as well as date of birth.
        </>
      ),
    },
    {
      question: "How much do clinical care visits cost?",
      answer: `If you're in-network with Elektra, you'll just have to pay the cost of your regular copay or coinsurance. If you're out-of-network, the cash pay price is $${env.VITE_INITIAL_VISIT_COST} for your initial visit (30 minutes), followed by $${env.VITE_FOLLOWUP_VISIT_COST} for subsequent 15-minute visits.`,
    },
    {
      question: "How much do coaching visits cost?",
      answer: `If you have a plan that covers coaching, your visit will cost you $0. Otherwise, coaching costs $${env.VITE_COACH_VISIT_COST}/session.`,
    },
    {
      question: "Can I use my HSA or FSA for my visits?",
      answer:
        "Yep, you can use your HSA or FSA card to pay for Elektra care visits!",
    },
    {
      question: "How will I be billed?",
      answer: (
        <>
          Insurance is complicated, and co-pays, deductibles, and co-insurance
          vary depending on your plan. If you have any questions regarding your
          financial responsibility, we recommend contacting your insurance plan.
          You can also text an image of the front and back of your card, as well
          as date of birth, to Elektra's insurance verification number{" "}
          <a
            href="sms:4148886578"
            className="text-violet underline hover:text-violet-700"
          >
            (414) 888-6578
          </a>
          .
        </>
      ),
    },
  ],
  "Education & Community": [
    {
      question:
        "What role does education and community play in the Elektra experience?",
      answer: (
        <>
          When it comes to our health, knowledge is power. That's why at
          Elektra, you have access to 100+ hours of MD-vetted education,
          readings, video content, and more. We also know that menopause can
          feel isolating, which is why community is so vital. In addition to
          accessing exclusive content, you have access to our{" "}
          <a
            href="https://elektrahealth.mn.co/spaces/9296667/feed"
            target="_blank"
            className="text-violet underline hover:text-violet-700"
            rel="noopener noreferrer"
            aria-label="Moderated feed"
          >
            moderated feed
          </a>{" "}
          where you can hear from like-minded women and attend expert events.
        </>
      ),
    },
  ],
};
